import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  forwardRef,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Helper, SpeciesCode, DetectMobile } from '@app/shared/utils';
import { LanguageCode } from '@app/shared/utils/enums/language-code.enum';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import { CoreFacade } from '@app/store/core/core.facade';
import { environment } from '@env/environment';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { IconName } from '@app/shared/utils/icon/icons';
import { IconComponent } from '../../utils/icon/icon.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@app/shared/pipes';
import { PdfPreviewService } from '@app/core/services/pdf-preview.service';
import { A4RatioHelper } from '@app/core/services/utils/a4-ratio-helper';

@Component({
  selector: 'app-bcs',
  templateUrl: './bcs.component.html',
  styleUrls: ['./bcs.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BcsComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, AsyncPipe, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BcsComponent implements ControlValueAccessor, OnChanges {
  BCS_Title = '';

  @Input() value: number;
  @Input() speciesCode: SpeciesCode = SpeciesCode.Unknown;
  @Input() minimalBCS = 1;
  @Input() isDisabled: boolean = false;
  @Input() showBCSInfo: boolean = true;

  speciesCode$ = new BehaviorSubject<SpeciesCode>(this.speciesCode);
  /**
   * Url for bcs pdf
   */
  bcsPdfUrl$ = combineLatest([this.coreFacade.bcsLanguages$, this.speciesCode$]).pipe(
    filter(([bcsLanguages, speciesCode]) => !!bcsLanguages?.length && !!speciesCode),
    map(([bcsLanguages, speciesCode]) => {
      const language = bcsLanguages?.find((item) => item === this.locale) || LanguageCode.enGB;
      return `${environment.blobUrl}/bcs/bcs-${speciesCode}-${language}.pdf?${environment.blobQueryParams}`;
    }),
  );

  DetectMobile = DetectMobile;

  activeSection: string = '';
  public readonly IconName = IconName;

  constructor(
    @Inject(LOCALE_ID) protected locale: string,
    private coreFacade: CoreFacade,
    private readonly pdfPreviewService: PdfPreviewService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  /**
   * Trigger speciesCode BehaviorSubject to update BCS pdf url
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.speciesCode) {
      this.speciesCode$.next(changes.speciesCode.currentValue);
    }
  }

  /** FormControl functions **/
  propagateChange = (_: any) => {
    // empty
  };
  onTouchedFn = () => {
    // empty
  };

  writeValue(bcs: number) {
    this.value = bcs;
    this.BCS_Title = this.getBCSTitle(bcs);
    this.changeDetectorRef.detectChanges();
  }

  registerOnChange(fn: (...args: unknown[]) => void) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: (...args: unknown[]) => void) {
    this.onTouchedFn = fn;
  }

  /**
   * Should be called when the control receives a disabled attribute
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.changeDetectorRef.detectChanges();
  }

  /** End Form Control functions **/

  largeBcsImageUrl() {
    if (this.value) {
      return IconName[`${this.speciesCode || 'dog'}_bcs_${this.value}_large`];
    }
    return IconName[`${this.speciesCode || 'dog'}_bcs_placeholder_large`];
  }

  mediumBcsImageUrl() {
    if (this.value) {
      return IconName[`${this.speciesCode || 'dog'}_bcs_${this.value}_medium`];
    }
    return IconName[`${this.speciesCode || 'dog'}_bcs_placeholder_medium`];
  }

  getBCSTitle(bcs: number): string {
    return Helper.formatBCSText(bcs);
  }

  BCS_Text_1() {
    return translateKey(`bcs_${this.speciesCode || 'dog'}_${this.value}_txt_1`);
  }

  BCS_Text_2() {
    return translateKey(`bcs_${this.speciesCode || 'dog'}_${this.value}_txt_2`);
  }

  BCS_Text_3() {
    return translateKey(`bcs_${this.speciesCode || 'dog'}_${this.value}_txt_3`);
  }

  BCS_Text_4() {
    return translateKey(`bcs_${this.speciesCode || 'dog'}_${this.value}_txt_4`).includes('bcs_')
      ? ''
      : translateKey(`bcs_${this.speciesCode}_${this.value}_txt_4`);
  }

  BCS_Text_5() {
    return translateKey(`bcs_${this.speciesCode || 'dog'}_${this.value}_txt_5`).includes('bcs_')
      ? ''
      : translateKey(`bcs_${this.speciesCode || 'dog'}_${this.value}_txt_5`);
  }

  selectBCS(bcs: number) {
    if (bcs >= this.minimalBCS) {
      this.writeValue(bcs);
      this.activeSection = this.calculateActiveSection();
      this.propagateChange(bcs);
      this.onTouchedFn();
      this.changeDetectorRef.detectChanges();
    }
  }

  getFillWidth(section: string): string {
    return this.activeSection === section ? '100%' : '0%';
  }

  generatePDF(bcsPdfUrl: string): void {
    event.preventDefault();
    this.pdfPreviewService.open(
      bcsPdfUrl,
      A4RatioHelper.getSizeInPixels(this.pdfPreviewService.getPdfContainerMaxHeight(), 'height', 'landscape'),
    );
  }

  private calculateActiveSection(): string {
    if (this.value >= 1 && this.value <= 3) return 'section-1';
    if (this.value >= 4 && this.value <= 5) return 'section-2';
    if (this.value >= 6 && this.value <= 7) return 'section-3';
    if (this.value >= 8 && this.value <= 9) return 'section-4';
    return '';
  }
}
